/**
 * Single source of truth for Uviva submission status label/variant —
 * replaces the copy pasted into SubmissionDetailDrawer.tsx,
 * UvivaSubmissionsPage.tsx, and UvivaDashboardPage.tsx (three drifting
 * copies of the same map, same shape problem as
 * sales-inbox/utils/stage-meta.ts was written to solve for that module).
 *
 * Internal roles (admin/operations/regulatory/finance) see the full
 * 9-value internal status, including exception states like "Post Failed".
 * The vendor (uviva role) sees the 2-bucket VendorStatus instead — see
 * middleware/app/api/routes/uviva.py's _vendor_status() for why: vendors
 * shouldn't have to parse or worry about our internal exception handling,
 * and critically should never see "Rejected" — a rejection is frequently
 * SPI's own exception handling to resolve, not something Uviva did wrong.
 */
import type { SubmissionStatus, VendorStatus } from '../api/types'

export type StatusChipVariant = 'success' | 'warning' | 'danger' | 'info' | 'neutral'

const INTERNAL_STATUS_VARIANT: Record<SubmissionStatus, StatusChipVariant> = {
  received: 'neutral',
  validating: 'info',
  validated: 'neutral',
  posting: 'info',
  posted: 'success',
  pending_review: 'warning',
  rejected: 'danger',
  post_failed: 'danger',
  validation_failed: 'danger',
}

const INTERNAL_STATUS_LABEL: Record<SubmissionStatus, string> = {
  received: 'Received',
  validating: 'Validating',
  validated: 'Validated',
  posting: 'Posting',
  posted: 'Posted',
  pending_review: 'Pending Review',
  rejected: 'Rejected',
  post_failed: 'Post Failed',
  validation_failed: 'Validation Failed',
}

const VENDOR_STATUS_VARIANT: Record<VendorStatus, StatusChipVariant> = {
  pending: 'warning',
  posted: 'success',
}

const VENDOR_STATUS_LABEL: Record<VendorStatus, string> = {
  pending: 'Pending',
  posted: 'Posted',
}

export function internalStatusVariant(status: string): StatusChipVariant {
  return INTERNAL_STATUS_VARIANT[status as SubmissionStatus] ?? 'neutral'
}

export function internalStatusLabel(status: string): string {
  return INTERNAL_STATUS_LABEL[status as SubmissionStatus] ?? status
}

export function vendorStatusVariant(vendorStatus: VendorStatus): StatusChipVariant {
  return VENDOR_STATUS_VARIANT[vendorStatus]
}

export function vendorStatusLabel(vendorStatus: VendorStatus): string {
  return VENDOR_STATUS_LABEL[vendorStatus]
}

/** Pick the right label/variant pair for the current viewer in one call. */
export function displayStatus(
  submission: { status: SubmissionStatus; vendor_status: VendorStatus },
  isVendorView: boolean,
): { label: string; variant: StatusChipVariant } {
  return isVendorView
    ? { label: vendorStatusLabel(submission.vendor_status), variant: vendorStatusVariant(submission.vendor_status) }
    : { label: internalStatusLabel(submission.status), variant: internalStatusVariant(submission.status) }
}
